import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, inr, imageUrl, isProductInStock } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, Plus, Minus, ShoppingBag, ChevronRight, FileDown, Sparkles, X, ListChecks } from "lucide-react";
import { toast } from "sonner";

export default function QuickOrderPage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openCats, setOpenCats] = useState([]); // list of category ids currently open
  const sectionRefs = useRef({});
  const navigate = useNavigate();
  const { items, addItem, setQty, getItem, totals } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/categories"),
          api.get("/products", { params: { limit: 500 } }),
        ]);
        setCategories(c.data);
        setProducts(p.data.filter(isProductInStock));
      } finally { setLoading(false); }
    })();
  }, []);

  // Follow the store taxonomy: a parent with subcategories is represented by
  // its subcategories; a parent without them is represented directly.
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filter = (p) => !q || p.name.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q);
    const map = {};
    for (const p of products) {
      if (!filter(p)) continue;
      const key = p.subcategory_id || p.category_id;
      (map[key] ||= []).push(p);
    }
    const childrenByParent = categories.reduce((result, category) => {
      if (category.parent_id) (result[category.parent_id] ||= []).push(category);
      return result;
    }, {});
    return categories.filter((category) => !category.parent_id).flatMap((category) => {
      const children = childrenByParent[category.id] || [];
      return children.length
        ? children.map((child) => ({ category: child, parent: category, items: map[child.id] || [] }))
        : [{ category, parent: null, items: map[category.id] || [] }];
    });
  }, [categories, products, query]);

  // Auto-expand categories matching a search
  useEffect(() => {
    if (!query.trim()) return;
    const ids = grouped.filter((g) => g.items.length > 0).map((g) => g.category.id);
    setOpenCats(ids);
  }, [query, grouped]);

  const nonEmpty = grouped.filter((g) => g.items.length > 0);
  const expandAll = () => setOpenCats(nonEmpty.map((g) => g.category.id));
  const collapseAll = () => setOpenCats([]);

  const scrollToCat = (id) => {
    if (!openCats.includes(id)) setOpenCats((s) => [...s, id]);
    setTimeout(() => {
      sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  // Per-category item count in cart
  const catItemCounts = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (it.kind !== "product") continue;
      const cid = it.subcategory_id || it.category_id;
      if (!cid) continue;
      map[cid] = (map[cid] || 0) + it.qty;
    }
    return map;
  }, [items]);

  const downloadPriceList = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html><head><title>Hyderabad Crackers · Price List</title>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>
        body{font-family:'Montserrat',sans-serif;padding:24px;color:#111}
        h1{color:#E53935;font-family:'Montserrat',sans-serif;border-bottom:2px solid #FFD700;padding-bottom:8px}
        h2{color:#111;margin-top:24px;background:#111;color:#FFD700;padding:6px 10px;border-radius:4px}
        table{width:100%;border-collapse:collapse;margin-top:6px;font-size:13px}
        th,td{padding:6px 8px;border-bottom:1px solid #ddd;text-align:left}
        th{background:#f5f5f5}
        .r{text-align:right}
        .mrp{color:#999;text-decoration:line-through;font-size:12px}
      </style></head><body>
      <h1>Hyderabad Crackers · Price List</h1>
      <div style="font-size:12px;color:#666">Generated ${new Date().toLocaleString()}</div>
      ${nonEmpty.map((g) => `
        <h2>${g.category.name} (${g.items.length})</h2>
        <table>
          <thead><tr><th>Product</th><th class="r">MRP</th><th class="r">Price</th><th class="r">Off</th></tr></thead>
          <tbody>
            ${g.items.map((p) => {
              const off = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
              return `<tr><td>${p.name}${p.brand ? " · " + p.brand : ""}</td>
                <td class="r"><span class="mrp">₹${p.mrp}</span></td>
                <td class="r"><b>₹${p.price}</b></td>
                <td class="r">${off ? off + "%" : ""}</td></tr>`;
            }).join("")}
          </tbody>
        </table>
      `).join("")}
      </body></html>
    `);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div data-testid="quick-order-page" className="max-w-6xl mx-auto px-4 py-4 md:pr-[320px]">
      {/* Toolbar */}
      <div className="sticky top-[64px] z-30 -mx-4 px-4 py-3 bg-hyd-bg/90 backdrop-blur-md border-b border-hyd-border">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-hyd-gold" /> Quick Order
          </h1>
          <span className="text-xs text-hyd-text2">Add fast · dense catalog</span>
          <div className="ml-auto flex gap-2 items-center">
            <Button
              data-testid="quick-price-list-btn"
              onClick={downloadPriceList}
              variant="secondary"
              className="bg-hyd-panel border border-hyd-border text-hyd-gold rounded-full h-9"
            >
              <FileDown className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Price list</span>
            </Button>
          </div>
        </div>
        <div className="mt-3 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-gold z-10" />
          <Input
            data-testid="quick-search"
            placeholder="Search across all products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 pr-9 h-11 bg-hyd-panel border-2 border-hyd-gold text-hyd-text rounded-2xl shadow-glow-gold focus-visible:ring-hyd-gold"
          />
          {query && (
            <button
              data-testid="quick-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-hyd-text2 hover:text-hyd-red"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category chip strip */}
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 fade-right-mask">
          {nonEmpty.map((g) => {
            const active = openCats.includes(g.category.id);
            const cnt = catItemCounts[g.category.id] || 0;
            return (
              <button
                key={g.category.id}
                data-testid={`quick-chip-${g.category.slug}`}
                onClick={() => scrollToCat(g.category.id)}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                  active ? "bg-hyd-gold text-hyd-bg border-hyd-gold" : "bg-hyd-panel border-hyd-border text-hyd-text2 hover:text-hyd-gold"
                }`}
              >
                <span>{g.category.name}</span>
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${cnt > 0 ? "bg-hyd-red text-white" : (active ? "bg-hyd-bg text-hyd-gold" : "bg-hyd-bg text-hyd-text3")}`}>
                  {cnt > 0 ? `${cnt} added` : g.items.length}
                </span>
              </button>
            );
          })}
          <div className="ml-auto shrink-0 flex gap-1 items-center">
            <button onClick={expandAll} data-testid="quick-expand-all" className="text-[11px] uppercase font-bold tracking-widest text-hyd-text2 hover:text-hyd-gold px-2">Expand all</button>
            <span className="text-hyd-text3">·</span>
            <button onClick={collapseAll} data-testid="quick-collapse-all" className="text-[11px] uppercase font-bold tracking-widest text-hyd-text2 hover:text-hyd-red px-2">Collapse all</button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="py-12 text-center text-hyd-text2">Loading catalog…</div>
      ) : (
        <Accordion type="multiple" value={openCats} onValueChange={setOpenCats} className="mt-5 space-y-3">
          {nonEmpty.map((g) => (
            <AccordionItem
              key={g.category.id}
              value={g.category.id}
              ref={(el) => { sectionRefs.current[g.category.id] = el; }}
              className="bg-hyd-panel border border-hyd-border rounded-2xl overflow-hidden"
            >
              <AccordionTrigger data-testid={`quick-section-${g.category.slug}`} className="px-4 hover:no-underline hover:bg-hyd-panel2/40">
                <div className="flex items-center gap-3 flex-1">
                  {g.category.image_url && (
                    <img src={imageUrl(g.category.image_url)} alt="" className="w-10 h-10 rounded-xl object-cover border border-hyd-border" />
                  )}
                  <div className="text-left">
                    <div className="font-display text-lg font-bold">{g.category.name}</div>
                    <div className="text-[11px] text-hyd-text3">{g.items.length} items</div>
                  </div>
                  {(catItemCounts[g.category.id] || 0) > 0 && (
                    <span className="ml-auto mr-3 text-[10px] font-bold uppercase tracking-widest bg-hyd-red/15 text-hyd-red border border-hyd-red/40 rounded-full px-2 py-0.5">
                      {catItemCounts[g.category.id]} added
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-0 pb-3 px-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {g.items.map((p) => <QuickRow key={p.id} product={p} getItem={getItem} addItem={addItem} setQty={setQty} />)}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
          {nonEmpty.length === 0 && (
            <div className="py-12 text-center text-hyd-text3">No products match &quot;{query}&quot;</div>
          )}
        </Accordion>
      )}

      {/* Sticky bar mobile */}
      <MobileCartBar totals={totals} onCheckout={() => navigate(items.length ? "/cart" : "/")} count={items.length} />

      {/* Desktop side panel */}
      <DesktopCartPanel totals={totals} count={items.length} />
    </div>
  );
}

function QuickRow({ product, getItem, addItem, setQty }) {
  const inCart = getItem("product", product.id);
  const qty = inCart?.qty || 0;
  const off = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const img = imageUrl(product.images?.[product.primary_image_index || 0] || product.images?.[0] || "");

  const handleAdd = () => {
    addItem({
      kind: "product", ref_id: product.id, name: product.name,
      unit_price: product.price, mrp: product.mrp, image_url: img,
      category_id: product.category_id, subcategory_id: product.subcategory_id, is_green_cracker: product.is_green_cracker,
    });
    toast.success(`${product.name.slice(0, 40)} added`, { duration: 1200 });
  };

  return (
    <div data-testid={`quick-row-${product.id}`} className="flex items-center gap-3 p-2 rounded-xl hover:bg-hyd-bg/70 border border-transparent hover:border-hyd-border">
      <div className="w-14 h-14 rounded-xl overflow-hidden bg-hyd-panel2 shrink-0 border border-hyd-border">
        {img ? <img src={img} alt="" loading="lazy" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-hyd-text3"><Sparkles className="w-6 h-6" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/products/${product.slug || product.id}`} className="font-semibold text-sm line-clamp-1 hover:text-hyd-gold">{product.name}</Link>
        <div className="text-[11px] text-hyd-text3 line-clamp-1">{product.unit || "1 Box"}{product.brand ? ` · ${product.brand}` : ""}</div>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-hyd-gold font-bold text-sm">{inr(product.price)}</span>
          {off > 0 && <>
            <span className="text-hyd-text3 line-through text-[11px]">{inr(product.mrp)}</span>
            <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/15 px-1 rounded">{off}% OFF</span>
          </>}
        </div>
      </div>
      <div className="shrink-0">
        {qty === 0 ? (
          <button
            data-testid={`quick-add-${product.id}`}
            onClick={handleAdd}
            className="bg-hyd-red text-white font-bold uppercase tracking-wider text-[11px] px-4 py-1.5 rounded-full shadow-glow-red hover:bg-hyd-redDim transition-colors animate-pop"
          >
            + Add
          </button>
        ) : (
          <div data-testid={`quick-stepper-${product.id}`} className="bg-hyd-bg border border-hyd-red flex items-center rounded-full px-1 py-1 gap-1 shadow-glow-red animate-pop">
            <button onClick={() => setQty("product", product.id, qty - 1)} aria-label="dec" className="w-6 h-6 grid place-items-center text-hyd-red rounded-full"><Minus className="w-3 h-3" /></button>
            <span className="min-w-[18px] text-center text-xs font-bold text-hyd-text">{qty}</span>
            <button onClick={() => setQty("product", product.id, qty + 1)} aria-label="inc" className="w-6 h-6 grid place-items-center text-hyd-gold rounded-full"><Plus className="w-3 h-3" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileCartBar({ totals, onCheckout, count }) {
  if (count === 0) return null;
  return (
    <div data-testid="quick-mobile-cartbar" className="md:hidden fixed bottom-3 left-3 right-3 z-40 animate-fade-in-up">
      <button
        onClick={onCheckout}
        className="w-full bg-hyd-gold text-hyd-bg rounded-2xl px-4 py-3 flex items-center justify-between shadow-glow-gold font-bold"
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <ShoppingBag className="w-5 h-5" />
            <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-hyd-red text-white text-[10px] grid place-items-center">
              {totals.count}
            </span>
          </div>
          <div className="text-left leading-tight">
            <div className="text-xs font-medium opacity-80">{totals.count} items · saved {inr(totals.savings)}</div>
            <div className="text-base">{inr(totals.subtotal)}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 uppercase tracking-wider text-sm">
          View Cart <ChevronRight className="w-4 h-4" />
        </div>
      </button>
    </div>
  );
}

function DesktopCartPanel({ totals, count }) {
  return (
    <aside data-testid="quick-desktop-cart" className="hidden md:block fixed right-4 top-[132px] w-[300px] max-h-[calc(100vh-160px)] overflow-hidden">
      <div className="bg-hyd-panel border border-hyd-border rounded-3xl overflow-hidden shadow-card-dark">
        <div className="px-4 py-3 border-b border-hyd-border bg-hyd-panel2/60 flex items-center gap-2">
          <ShoppingBag className="w-4 h-4 text-hyd-gold" />
          <span className="font-display text-lg font-bold">Your cart</span>
          <span className="ml-auto text-xs text-hyd-text2">{totals.count} items</span>
        </div>
        <div className="p-4 space-y-2 text-sm">
          <Row label="MRP total" value={<span className="line-through text-hyd-text3">{inr(totals.mrpTotal)}</span>} />
          <Row label="You save" value={<span className="text-emerald-400 font-bold">{inr(totals.savings)}</span>} />
          <Row label="Subtotal" value={inr(totals.subtotal)} />
          <Row label="Delivery" value={totals.deliveryFee === 0 ? "FREE" : inr(totals.deliveryFee)} valueClass={totals.deliveryFee === 0 ? "text-emerald-400" : ""} />
          {totals.deliveryFee > 0 && (
            <div className="text-[11px] text-hyd-text2">Add {inr(totals.freeDeliveryRemaining)} for free delivery</div>
          )}
          <div className="border-t border-hyd-border my-2" />
          <Row label="Total" value={inr(totals.total)} bold />
          <Link to={count ? "/cart" : "/"} className="mt-3 block">
            <Button className="w-full bg-hyd-red hover:bg-hyd-redDim text-white font-bold uppercase tracking-wider rounded-full">
              {count ? "View cart" : "Add items to start"}
            </Button>
          </Link>
          {count > 0 && (
            <div className="text-[11px] text-hyd-text2 text-center mt-2">
              You&apos;ll sign in on the next step to place the order
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function Row({ label, value, bold, valueClass }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-base" : "text-sm"}`}>
      <span className="text-hyd-text2">{label}</span>
      <span className={valueClass || "text-hyd-text"}>{value}</span>
    </div>
  );
}
